import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiCheck, FiInfo, FiX } from "react-icons/fi";

const APP_GUIDES = {
  documentation: {
    summary: "Find project references and read the documentation available to your workspace.",
    steps: [
      ["Find a document", "Search the documentation library by title or description."],
      ["Read or download", "Open the project guide where available, or download a listed document. Use the back arrow to return to the library."],
      ["Keep a reference open", "Use Open in new tab when available to read alongside your work."],
    ],
  },
  sow: {
    summary: "Review the statement of work for this engagement.",
    steps: [
      ["Review the scope", "Read the deliverables, responsibilities and exclusions in the protected document."],
      ["Check the terms", "Review milestones, assumptions and commercial terms before proceeding."],
      ["Resolve a question", "Use Project support for clarification and include the section you are referring to."],
    ],
  },
  ideas: {
    summary: "Capture ideas and develop them into scope for review.",
    steps: [
      ["Capture an idea", "Add an idea or open an existing one to describe the need and expected outcome."],
      ["Build the detail", "Keep scope, status and supporting files with the idea as it develops."],
      ["Prepare for review", "Check the idea details and outstanding questions before sharing it for review."],
    ],
    note: "Idea Builder must be enabled for this project. Available actions depend on your access.",
  },
  projects: {
    summary: "Track the tasks and delivery work for the current project.",
    steps: [
      ["Find the work", "Use the list or board view to find a task in this project."],
      ["Inspect a task", "Open a task to review its details, assignee, status and supporting files."],
      ["Keep progress current", "Create or update tasks and checklists when your project permissions allow it."],
    ],
    note: "Project Management must be enabled for this project. Only this project's work is shown.",
  },
  assets: {
    summary: "Find shared project documents and meeting recordings.",
    steps: [
      ["Find a resource", "Search by title or description, then filter by Documents or Recordings."],
      ["Download a file", "Use Download on a document to keep a local copy."],
      ["Watch a recording", "Open a recording in a new tab. Use the displayed passcode if the provider asks for it."],
    ],
  },
};

export default function DealDeskGuide({ app, iconOnly = false }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const triggerRef = useRef(null);
  const dialogRef = useRef(null);
  const headingRef = useRef(null);
  const closeRef = useRef(null);
  const doneRef = useRef(null);
  const backdropPress = useRef(false);
  const guide = APP_GUIDES[app.id];

  useEffect(() => {
    if (!open) return undefined;
    const dialog = dialogRef.current;
    const trigger = triggerRef.current;
    // The native top layer also makes embedded app frames inert.
    dialog.showModal();
    headingRef.current.focus();
    return () => {
      dialog.close();
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    };
  }, [open]);

  const handleKeyDown = event => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    } else if (event.key === "Tab") {
      const first = closeRef.current;
      const last = doneRef.current;
      if (event.shiftKey && (document.activeElement === first || document.activeElement === headingRef.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  return <>
    <button type="button" ref={triggerRef} className={`deal-desk-guide__trigger${iconOnly ? ' deal-desk-guide__trigger--icon' : ''}`}
      aria-label={`${app.label} guide`} title={`${app.label} guide`} aria-haspopup="dialog"
      aria-expanded={open} aria-controls={`${id}-dialog`} onClick={() => setOpen(true)}>
      <FiInfo aria-hidden="true" /><span>Guide</span>
    </button>
    {open && createPortal(<dialog ref={dialogRef} id={`${id}-dialog`} className="deal-desk-guide"
      aria-modal="true" aria-labelledby={`${id}-title`} aria-describedby={`${id}-summary`}
      onCancel={event => { event.preventDefault(); setOpen(false); }} onKeyDown={handleKeyDown}
      onPointerDown={event => { backdropPress.current = event.target === event.currentTarget; }}
      onClick={event => {
        if (backdropPress.current && event.target === event.currentTarget) setOpen(false);
        backdropPress.current = false;
      }}>
      <div className="deal-desk-guide__body">
        <header className="deal-desk-guide__header">
          <h2 id={`${id}-title`} ref={headingRef} tabIndex={-1}>{app.label} guide</h2>
          <button type="button" ref={closeRef} className="deal-desk-guide__close" aria-label="Close guide" title="Close guide" onClick={() => setOpen(false)}><FiX aria-hidden="true" /></button>
        </header>
        <p id={`${id}-summary`} className="deal-desk-guide__summary">{guide.summary}</p>
        <ol className="deal-desk-guide__steps">
          {guide.steps.map(([title, detail]) => <li key={title}><h3>{title}</h3><p>{detail}</p></li>)}
        </ol>
        {guide.note && <p className="deal-desk-guide__note">{guide.note}</p>}
        <footer className="deal-desk-guide__footer">
          <p>To ask the team or raise a case, use Project support. This guide does not send a request.</p>
          <button type="button" ref={doneRef} className="deal-desk-guide__done" onClick={() => setOpen(false)}><FiCheck aria-hidden="true" />Done</button>
        </footer>
      </div>
    </dialog>, document.body)}
  </>;
}
